/**
 * Order lifecycle stages — the canonical 1–29 stage model from the backend.
 * Ported verbatim from cpanel (`ui/order-single/constants.ts` +
 * `assets/data/order_stages.ts`). Stage numbers are a backend contract; the
 * labels here are the Russian source-of-truth and are surfaced through i18n keys
 * (`orders:stage.<id>`) at render time — see `lib/stage.ts`.
 */

/**
 * Stage numbers as a const object (TS `enum` is banned by `erasableSyntaxOnly`).
 * Use `OrderStage.PERFORMER_FOUND` exactly like an enum member.
 */
export const OrderStage = {
  NEW: 1,
  ESTIMATING: 2,
  READY_FOR_APPROVAL: 3,
  SCHEDULED: 4,
  PERFORMER_LOOK_UP: 5,
  PERFORMER_DRAFT: 6,
  PERFORMER_FOUND: 7,
  PICK_UP_ARRIVED: 8,
  READY_FOR_PICK_UP_CONFIRMATION: 9,
  PICKED_UP: 10,
  DELIVERY_ARRIVED: 11,
  READY_FOR_DELIVERY_CONFIRMATION: 12,
  PAY_WAITING: 13,
  DELIVERED: 14,
  DELIVERED_FINISH: 15,
  RETURNING: 16,
  RETURN_ARRIVED: 17,
  READY_FOR_RETURN_CONFIRMATION: 18,
  RETURNED: 19,
  RETURNED_FINISH: 20,
  CANCELLED_BY_TAXI: 21,
  CANCELLED: 22,
  CANCELLED_WITH_PAYMENT: 23,
  CANCELLED_WITH_ITEMS_ON_HAND: 24,
  FAILED: 25,
  ESTIMATING_FAILED: 26,
  PERFORMER_NOT_FOUND: 27,
  CANCELLED_OUT_OF_ZONE: 28,
  NOT_SUITABLE: 29,
} as const;

export type OrderStageValue = (typeof OrderStage)[keyof typeof OrderStage];

/** Lowest and highest valid stage numbers (inclusive). */
export const MIN_STAGE = 1;
export const MAX_STAGE = 29;

export interface OrderStageMeta {
  number: number;
  /** Brand-tinted hex used for the status badge background in cpanel. */
  color: string;
  /** Russian label (source of truth; i18n key derived from `id`). */
  name: string;
  /** Stable slug used to derive the i18n key (`orders:stage.<id>`). */
  id: string;
}

/**
 * Per-stage presentation metadata (color + label + slug). Ported from cpanel's
 * live `order_stages` export (NOT the commented-out legacy block). The status
 * column looks a row up by `number`.
 */
export const ORDER_STAGES: readonly OrderStageMeta[] = [
  { number: 1, color: '#334155', name: 'Новый', id: 'stage_new' },
  { number: 2, color: '#F2C94C', name: 'Оценка', id: 'stage_estimating' },
  { number: 3, color: '#EAB308', name: 'Утверждение', id: 'stage_ready_for_approval' },
  { number: 4, color: '#cbc800', name: 'Запланирован', id: 'stage_scheduled' },
  { number: 5, color: '#FB923C', name: 'Начало поиска', id: 'stage_performer_look_up' },
  { number: 6, color: '#F97316', name: 'Начало поиска', id: 'stage_performer_draft' },
  {
    number: 7,
    color: 'var(--primary)',
    name: 'Курьер найден',
    id: 'stage_performer_found',
  },
  {
    number: 8,
    color: '#0EA5E9',
    name: 'Прибыл к отправителю',
    id: 'stage_pick_up_arrived',
  },
  {
    number: 9,
    color: '#0284C7',
    name: 'Подтв. получения',
    id: 'stage_ready_for_pick_up_confirmation',
  },
  { number: 10, color: '#0369A1', name: 'Забрал заказ', id: 'stage_picked_up' },
  {
    number: 11,
    color: '#4333EA',
    name: 'Прибыл к получателю',
    id: 'stage_delivery_arrived',
  },
  {
    number: 12,
    color: '#2222CE',
    name: 'Подтв. доставки',
    id: 'stage_ready_for_delivery_confirmation',
  },
  { number: 13, color: '#1B2559', name: 'Ожидание оплаты', id: 'stage_pay_waiting' },
  { number: 14, color: '#422AFB', name: 'Доставлен', id: 'stage_delivered' },
  { number: 15, color: '#01B574', name: 'Заказ завершен', id: 'stage_delivered_finish' },
  { number: 16, color: '#EB5757', name: 'Возврат', id: 'stage_returning' },
  { number: 17, color: '#DB5151', name: 'Прибыл на возврат', id: 'stage_return_arrived' },
  {
    number: 18,
    color: '#FF0844',
    name: 'Подтв. возврата',
    id: 'stage_ready_for_return_confirmation',
  },
  { number: 19, color: '#991B1B', name: 'Возвращено', id: 'stage_returned' },
  { number: 20, color: '#7F1D1D', name: 'Возврат завершен', id: 'stage_returned_finish' },
  { number: 21, color: '#1B2559', name: 'Отменено такси', id: 'stage_cancelled_by_taxi' },
  { number: 22, color: '#1B2559', name: 'Заказ отменен', id: 'stage_cancelled' },
  {
    number: 23,
    color: '#f00',
    name: 'Баланс отрицательный',
    id: 'stage_cancelled_with_payment',
  },
  {
    number: 24,
    color: '#1B2559',
    name: 'Отменен с возвратом',
    id: 'stage_cancelled_with_items_on_hand',
  },
  { number: 25, color: '#1B2559', name: 'Не удалось', id: 'stage_failed' },
  { number: 26, color: '#E1E9F8', name: 'Не оценено', id: 'stage_estimating_failed' },
  {
    number: 27,
    color: '#536088',
    name: 'Курьер не найден',
    id: 'stage_performer_not_found',
  },
  {
    number: 28,
    color: '#1B2559',
    name: 'Вне зоны доставки',
    id: 'stage_cancelled_out_of_zone',
  },
  { number: 29, color: '#FF6347', name: 'Не подходит', id: 'stage_not_suitable' },
];

/** Fast lookup of stage metadata by stage number. */
export const ORDER_STAGE_BY_NUMBER = new Map<number, OrderStageMeta>(
  ORDER_STAGES.map((stage) => [stage.number, stage]),
);

/* ── Stage groupings that gate row actions & timers (ported verbatim) ── */

/** Stages where the "finish order" button is hidden because the order is done. */
export const FINISHED_STAGES = [15, 20, 22, 23, 24] as const;

/** Stages where the "finish order" button is explicitly blocked. */
export const BLOCKED_FINISH_BUTTON_STAGES = [20, 27, 5, 6, 4] as const;

/** Stages where an order may be cancelled from the list (cancel icon shown). */
export const ORDER_CANCELLED_STAGES = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
] as const;

/** Stages where a courier may be re-ordered (re-order icon shown). */
export const ORDER_REORDER_STAGES = [22, 23, 27] as const;

/** Stages with a live courier on the map → enable 5 s location polling. */
export const LIVE_TRACKING_STAGES = [7, 8, 9, 10, 11, 12, 13, 14] as const;

/** Stages whose status cell renders a timer badge instead of a status badge. */
export const CURRENT_TIMER_STAGES = [
  5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 18, 19,
] as const;

/** Stages where the elapsed-time timer is frozen (`onlyShow`). */
export const TIMER_STOPPED_STAGES = [
  1, 2, 3, 15, 20, 21, 22, 23, 24, 25, 26, 27, 28,
] as const;

/**
 * Stages where the "switch courier payment to balance" action is offered.
 * `DELIVERED` (14) and `RETURNED` (19) — ported from cpanel's
 * `PAYMENT_TO_BALANCE_STAGES`. Named here so the value lives with the other
 * stage groupings instead of being duplicated in the detail components.
 */
export const PAYMENT_TO_BALANCE_STAGES = [
  OrderStage.DELIVERED,
  OrderStage.RETURNED,
] as const;

/* ── Sub-tab rows under CURRENT / SEARCH / CANCELLED ── */

/**
 * Accent hue for a sub-tab's dot + the matching status-group badge. Ported
 * from the cpanel `accent` hexes onto design-token hues: the UI maps each
 * slug to a token-backed class, so no literal colour lives here.
 */
export type OrderAccent =
  | 'neutral'
  | 'pending'
  | 'active'
  | 'sky'
  | 'info'
  | 'violet'
  | 'teal'
  | 'danger';

/**
 * Delivery-stage bands shown as a second tab row under the CURRENT (active
 * orders) tab. The `value` is sent to the backend as `current_status_tab`;
 * `labelKey` resolves to a `orders:timer.<key>` i18n string (the bands reuse the
 * timer-group labels). Ported from cpanel `STAGE_TAB_DEFS` (accents
 * #EA580C / #0EA5E9 / #0284C7 / #422AFB → active / sky / info / violet).
 */
export const ORDER_STAGE_TABS = [
  { value: 'to_origin', labelKey: 'timer.heading_to_sender', accent: 'active' },
  { value: 'wait_order', labelKey: 'timer.waiting_order', accent: 'sky' },
  { value: 'to_destination', labelKey: 'timer.heading_to_recipient', accent: 'info' },
  { value: 'wait_receiver', labelKey: 'timer.waiting_recipient', accent: 'violet' },
] as const satisfies readonly { value: string; labelKey: string; accent: OrderAccent }[];

export type OrderStageTab = (typeof ORDER_STAGE_TABS)[number]['value'];

/** Default band selected when the CURRENT tab opens (cpanel: `to_origin`). */
export const DEFAULT_STAGE_TAB: OrderStageTab = 'to_origin';

/**
 * Equipment sub-tabs under the in-search (SEARCH) tab — cpanel
 * `EQUIPMENT_TAB_DEFS`. `all` lists every in-search order; `scooter`/`car`
 * are sent as `equipment_scope` (`car` is the single car equipment, `scooter`
 * every other one).
 */
export const ORDER_EQUIPMENT_TABS = [
  { value: 'all', labelKey: 'equipmentTab.all', accent: 'neutral' },
  { value: 'scooter', labelKey: 'equipmentTab.scooter', accent: 'info' },
  { value: 'car', labelKey: 'equipmentTab.car', accent: 'violet' },
] as const satisfies readonly { value: string; labelKey: string; accent: OrderAccent }[];

export type OrderEquipmentTab = (typeof ORDER_EQUIPMENT_TABS)[number]['value'];
export const DEFAULT_EQUIPMENT_TAB: OrderEquipmentTab = 'all';

/**
 * Who-cancelled sub-tabs under CANCELLED — cpanel `CANCELLED_BY_ROLE_TAB_DEFS`.
 * `VENDOR` / `USER` are sent as `cancelled_by_roles`; `all` omits it.
 */
export const ORDER_CANCELLED_ROLE_TABS = [
  { value: 'all', labelKey: 'cancelledRoleTab.all', accent: 'neutral' },
  { value: 'VENDOR', labelKey: 'cancelledRoleTab.VENDOR', accent: 'active' },
  { value: 'USER', labelKey: 'cancelledRoleTab.USER', accent: 'teal' },
] as const satisfies readonly { value: string; labelKey: string; accent: OrderAccent }[];

export type OrderCancelledRoleTab = (typeof ORDER_CANCELLED_ROLE_TABS)[number]['value'];
export const DEFAULT_CANCELLED_ROLE_TAB: OrderCancelledRoleTab = 'all';

/**
 * Cached counts per who-cancelled sub-tab. Partial: cpanel has no counts
 * endpoint for these — each badge fills in from the list response of that
 * sub-tab (`count`), and `all` from `count_info.cancelled`.
 */
export type OrderCancelledRoleCounts = Partial<Record<OrderCancelledRoleTab, number>>;
